import { useCallback, useEffect, useState } from "react";
import { useRealtimeKitSelector } from "@cloudflare/realtimekit-react";
import type RTKClient from "@cloudflare/realtimekit";
import type { RTKParticipant, StageStatus } from "@cloudflare/realtimekit";

export type LivestreamStageRequest = {
  displayName: string;
  userId: string;
  peerId: string;
};

function stageStatusLabel({ status }: { status: StageStatus | undefined }): string {
  switch (status) {
    case "ON_STAGE":
      return "On stage";
    case "ACCEPTED_TO_JOIN_STAGE":
      return "Accepted";
    case "REQUESTED_TO_JOIN_STAGE":
      return "Requested";
    case "OFF_STAGE":
    default:
      return "Viewer";
  }
}

export function useLivestreamStageHost({
  meeting,
}: {
  meeting: RTKClient | undefined;
}) {
  const selfId = useRealtimeKitSelector((m) => m.self.id);
  const stageStatus = useRealtimeKitSelector((m) => m.stage.status);
  const joinedParticipants = useRealtimeKitSelector((m) =>
    m.participants.joined.toArray(),
  );
  const [stageRequests, setStageRequests] = useState<LivestreamStageRequest[]>([]);
  const [actingOnUserId, setActingOnUserId] = useState<string | null>(null);

  useEffect(() => {
    if (!meeting?.stage || stageStatus === "ON_STAGE") return;
    void meeting.stage.join();
  }, [meeting, stageStatus]);

  useEffect(() => {
    if (!meeting?.stage || stageStatus !== "ON_STAGE") return;

    let cancelled = false;

    void (async () => {
      if (!meeting.self.audioEnabled) await meeting.self.enableAudio();
      if (cancelled) return;
      if (!meeting.self.videoEnabled) await meeting.self.enableVideo();
    })();

    return () => {
      cancelled = true;
    };
  }, [meeting, stageStatus]);

  useEffect(() => {
    if (!meeting?.stage) return;

    const syncRequests = (requests?: LivestreamStageRequest[]) => {
      if (requests) {
        setStageRequests(requests);
        return;
      }
      const { stageRequests: current } = meeting.stage.getAccessRequests();
      setStageRequests(current);
    };

    syncRequests();
    meeting.stage.on("stageAccessRequestUpdate", syncRequests);
    return () => {
      meeting.stage.off("stageAccessRequestUpdate", syncRequests);
    };
  }, [meeting]);

  const grantAccess = useCallback(
    async ({ userId }: { userId: string }) => {
      if (!meeting?.stage || actingOnUserId) return;
      setActingOnUserId(userId);
      try {
        await meeting.stage.grantAccess([userId]);
      } finally {
        setActingOnUserId(null);
      }
    },
    [actingOnUserId, meeting],
  );

  const denyAccess = useCallback(
    async ({ userId }: { userId: string }) => {
      if (!meeting?.stage || actingOnUserId) return;
      setActingOnUserId(userId);
      try {
        await meeting.stage.denyAccess([userId]);
      } finally {
        setActingOnUserId(null);
      }
    },
    [actingOnUserId, meeting],
  );

  const removeFromStage = useCallback(
    async ({ userId }: { userId: string }) => {
      if (!meeting?.stage || actingOnUserId) return;
      setActingOnUserId(userId);
      try {
        await meeting.stage.kick([userId]);
      } finally {
        setActingOnUserId(null);
      }
    },
    [actingOnUserId, meeting],
  );

  const participants = joinedParticipants.map((participant: RTKParticipant) => ({
    id: participant.id,
    userId: participant.userId,
    name: participant.name?.trim() || "Guest",
    isSelf: participant.id === selfId,
    stageStatus: participant.stageStatus,
    stageLabel: stageStatusLabel({ status: participant.stageStatus }),
  }));

  return {
    participants,
    stageRequests,
    grantAccess,
    denyAccess,
    removeFromStage,
    actingOnUserId,
  };
}
